import { useCallback, useState, useRef, useEffect } from "react";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  useEditor,
} from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import { Menu, UnstyledButton } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";

/**
 * Normalizes a variable name extracted from {{...}} tokens or data-variable
 * attributes, stripping HTML entities, non-breaking spaces, and other
 * characters that don't belong in an identifier.
 *
 * Examples:
 *   "fibers\u00a0mm"  → "fibers_mm"
 *   "fibers&nbsp;mm"  → "fibers_mm"
 *   "  status  "      → "status"
 */
export function normalizeVariableName(raw: string): string {
  if (!raw) return raw;
  // Decode named HTML entities that commonly appear (e.g. &nbsp; &amp;)
  let name = raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#160;/g, " "); // numeric non-breaking space
  // Replace non-breaking space (U+00A0) and other whitespace variants with plain space
  name = name.replace(/[\u00a0\u200b\u00ad]/g, " ");
  // Trim surrounding whitespace
  name = name.trim();
  // Replace internal spaces/whitespace runs with underscore
  name = name.replace(/\s+/g, "_");
  // Strip anything that isn't alphanumeric, underscore, or hyphen
  name = name.replace(/[^\w-]/g, "");
  return name.toLowerCase();
}

export interface VariableFieldConfig {
  options: Array<{ value: string; label: string }>;
  value: string | null;
  onChange: (val: string) => void;
  readOnly?: boolean;
}

export type VariableConfig = Record<string, VariableFieldConfig>;

export function focusAdjacentVariableInput(
  current: HTMLElement,
  backwards: boolean
): boolean {
  const editorRoot =
    current.closest("[data-clinical-rich-text-editor]") || document;
  const inputs = Array.from(
    editorRoot.querySelectorAll<HTMLElement>("[data-variable-input]")
  );
  const currentIndex = inputs.indexOf(current);
  const nextIndex = backwards ? currentIndex - 1 : currentIndex + 1;
  const next = inputs[nextIndex];
  if (!next) return false;
  next.focus();
  return true;
}

/**
 * Resolves all <span data-variable="name" data-variable-value="val"> chips in
 * serialized TipTap HTML by substituting filled-in values. Falls back to
 * entries in the optional `extraVars` map (for system/context variables), then
 * leaves the token as-is if neither source has a value.
 *
 * Use this before displaying TipTap HTML outside of the editor (report preview,
 * results widget, document view, etc.).
 */
export function resolveVariableChipValues(
  html: string,
  extraVars?: Record<string, string | null | undefined>
): string {
  if (!html) return html;

  return html.replace(
    /<span([^>]*data-variable="([^"]+)"[^>]*)>[^<]*<\/span>/gi,
    (fullMatch, attrs: string, rawName: string) => {
      const name = normalizeVariableName(rawName);

      // 1. data-variable-value on the span (user typed a value)
      const dvMatch = attrs.match(/data-variable-value="([^"]*)"/);
      const dvVal = dvMatch?.[1]?.trim();
      if (dvVal) return dvVal;

      // 2. extra vars map (system/case context variables)
      const ctxVal = extraVars?.[name]?.trim();
      if (ctxVal) return ctxVal;

      // 3. leave as-is (preserve original span so it can still be edited)
      return fullMatch;
    }
  );
}

/**
 * Converts {{VARIABLE}} tokens in raw HTML to <span data-variable="..."> so the
 * VariableChip TipTap node can parse and render them as styled chips.
 * Idempotent: strips existing wrappers first to prevent double-wrapping.
 * Preserves data-variable-value attributes through the round-trip.
 */
export function wrapVariablesAsChips(html: string): string {
  if (!html) return html;

  const savedValues = new Map<string, string[]>();
  const valueExtractor =
    /<span([^>]*data-variable="([^"]*)"[^>]*)>\{\{[^}]+\}\}<\/span>/g;
  Array.from(html.matchAll(valueExtractor)).forEach((match) => {
    const attrs = match[1];
    const name = match[2];
    const valMatch = attrs.match(/data-variable-value="([^"]*)"/);
    if (!savedValues.has(name)) savedValues.set(name, []);
    savedValues.get(name)!.push(valMatch?.[1] || "");
  });

  const stripped = html.replace(
    /<span[^>]*data-variable="[^"]*"[^>]*>\{\{([^}]+)\}\}<\/span>/g,
    "{{$1}}"
  );

  const usedIndices = new Map<string, number>();
  return stripped.replace(/\{\{([^}]+)\}\}/g, (_, rawName: string) => {
    const name = normalizeVariableName(rawName);
    if (!name) return "";
    const values = savedValues.get(name);
    const idx: number = usedIndices.get(name) ?? 0;
    usedIndices.set(name, idx + 1);
    const val = values?.[idx] || "";
    if (val) {
      return `<span data-variable="${name}" data-variable-value="${val}">{{${name}}}</span>`;
    }
    return `<span data-variable="${name}">{{${name}}}</span>`;
  });
}

function VariableChipView({
  node,
  editor,
  updateAttributes,
}: {
  node: { attrs: { name: string; value: string | null } };
  editor: ReturnType<typeof useEditor>;
  updateAttributes: (attrs: Record<string, unknown>) => void;
}) {
  const varName: string = node.attrs.name;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config: VariableConfig | undefined = (editor as any)?.storage
    ?.variableChip?.config;
  const fieldConfig: VariableFieldConfig | undefined = config?.[varName];

  const [localValue, setLocalValue] = useState<string | null>(
    node.attrs.value ?? fieldConfig?.value ?? null
  );
  const freeTextRef = useRef<HTMLInputElement>(null);
  const handleVariableInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
        return;
      }
      if (
        event.key === "Tab" &&
        focusAdjacentVariableInput(event.currentTarget, event.shiftKey)
      ) {
        event.preventDefault();
      }
    },
    []
  );

  const latestConfig = (): VariableFieldConfig | undefined => {
    const cfg: VariableConfig | undefined = (editor as any)?.storage
      ?.variableChip?.config;
    return cfg?.[varName];
  };

  // Sync localValue and DOM input when node attrs change externally
  // (e.g., from NumberInput updating the chip via setNodeMarkup).
  // ProseMirror nodes are immutable — setNodeMarkup creates a new node,
  // so node.attrs.value is a reliable dependency.
  useEffect(() => {
    const nodeVal = node.attrs.value;
    if (nodeVal != null && nodeVal !== localValue) {
      setLocalValue(nodeVal);
      if (freeTextRef.current) {
        freeTextRef.current.value = nodeVal;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.attrs.value]);

  // When the chip mounts with no persisted value but the config already has a
  // value (e.g. subsite pre-populated from accessioning), write it back to the
  // TipTap node so the serialized HTML includes data-variable-value and sign-out
  // validation doesn't flag the chip as unresolved.
  useEffect(() => {
    const configVal = latestConfig()?.value?.trim();
    if (configVal && !node.attrs.value) {
      updateAttributes({ value: configVal });
      setLocalValue(configVal);
    }
    // Re-run whenever the external config value changes so newly-loaded subsite
    // data also gets persisted without requiring the user to re-select.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfig?.value]);

  const chipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "var(--mantine-color-blue-1)",
    color: "var(--mantine-color-blue-8)",
    padding: "1px 6px",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: "0.85em",
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: fieldConfig ? "pointer" : "default",
    userSelect: "none",
    verticalAlign: "middle",
  };

  if (fieldConfig) {
    if (fieldConfig.readOnly) {
      const resolvedValue = fieldConfig.value?.trim() || `{{${varName}}}`;
      const isResolved = !!fieldConfig.value?.trim();
      return (
        <NodeViewWrapper
          as="span"
          style={{ display: "inline" }}
          contentEditable={false}
        >
          <span
            style={{
              ...chipStyle,
              backgroundColor: isResolved
                ? "var(--mantine-color-green-1)"
                : "var(--mantine-color-orange-1)",
              color: isResolved
                ? "var(--mantine-color-green-8)"
                : "var(--mantine-color-orange-8)",
              cursor: "default",
            }}
          >
            {resolvedValue}
          </span>
        </NodeViewWrapper>
      );
    }

    const hasOptions = fieldConfig.options.length > 0;

    if (!hasOptions) {
      const handleChange = (val: string) => {
        setLocalValue(val);
        updateAttributes({ value: val || null });
        const live = latestConfig();
        (live ?? fieldConfig).onChange(val);
      };

      const placeholder = varName.replace(/_/g, " ");

      return (
        <NodeViewWrapper
          as="span"
          style={{ display: "inline-flex", alignItems: "center" }}
          contentEditable={false}
        >
          <input
            ref={freeTextRef}
            data-variable-input
            type="text"
            defaultValue={localValue || ""}
            placeholder={placeholder}
            onInput={(e) => handleChange(e.currentTarget.value)}
            onKeyDown={handleVariableInputKeyDown}
            style={{
              display: "inline-block",
              width: Math.max(60, ((localValue || placeholder).length + 1) * 8),
              border: localValue
                ? "1px solid var(--mantine-color-green-4)"
                : "1px solid var(--mantine-color-orange-4)",
              borderRadius: 4,
              padding: "1px 6px",
              fontFamily: "monospace",
              fontSize: "0.85em",
              fontWeight: 600,
              backgroundColor: localValue
                ? "var(--mantine-color-green-0)"
                : "var(--mantine-color-orange-0)",
              color: localValue
                ? "var(--mantine-color-green-8)"
                : "var(--mantine-color-dark-4)",
              outline: "none",
              verticalAlign: "middle",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--mantine-color-blue-5)";
              e.currentTarget.style.boxShadow =
                "0 0 0 1px var(--mantine-color-blue-2)";
            }}
            onBlurCapture={(e) => {
              const filled = !!e.currentTarget.value;
              e.currentTarget.style.borderColor = filled
                ? "var(--mantine-color-green-4)"
                : "var(--mantine-color-orange-4)";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </NodeViewWrapper>
      );
    }

    const currentLabel = localValue || varName;

    return (
      <NodeViewWrapper
        as="span"
        style={{ display: "inline" }}
        contentEditable={false}
      >
        <Menu withinPortal position="bottom-start" shadow="md">
          <Menu.Target>
            <UnstyledButton
              component="span"
              data-variable-input
              tabIndex={0}
              style={chipStyle}
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
            >
              {currentLabel}
              <IconChevronDown size={10} style={{ marginLeft: 2 }} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {fieldConfig.options.map((opt) => (
              <Menu.Item
                key={opt.value}
                fw={localValue === opt.label ? 700 : undefined}
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  setLocalValue(opt.label);
                  updateAttributes({ value: opt.label || null });
                  const live = latestConfig();
                  (live ?? fieldConfig).onChange(opt.label);
                }}
              >
                {opt.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </NodeViewWrapper>
    );
  }

  // No config yet (storage not populated) -- render as editable free-text input.
  // Must still call updateAttributes so values typed before config arrives are persisted.
  const handleFallbackChange = (val: string) => {
    setLocalValue(val);
    updateAttributes({ value: val || null });
  };

  const placeholder = varName.replace(/_/g, " ");
  return (
    <NodeViewWrapper
      as="span"
      style={{ display: "inline-flex", alignItems: "center" }}
      contentEditable={false}
    >
      <input
        data-variable-input
        type="text"
        defaultValue={localValue || ""}
        placeholder={placeholder}
        onInput={(e) => handleFallbackChange(e.currentTarget.value)}
        onKeyDown={handleVariableInputKeyDown}
        style={{
          display: "inline-block",
          width: Math.max(60, ((localValue || placeholder).length + 1) * 8),
          border: localValue
            ? "1px solid var(--mantine-color-green-4)"
            : "1px solid var(--mantine-color-orange-4)",
          borderRadius: 4,
          padding: "1px 6px",
          fontFamily: "monospace",
          fontSize: "0.85em",
          fontWeight: 600,
          backgroundColor: localValue
            ? "var(--mantine-color-green-0)"
            : "var(--mantine-color-orange-0)",
          color: localValue
            ? "var(--mantine-color-green-8)"
            : "var(--mantine-color-dark-4)",
          outline: "none",
          verticalAlign: "middle",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "var(--mantine-color-blue-5)";
          e.currentTarget.style.boxShadow =
            "0 0 0 1px var(--mantine-color-blue-2)";
        }}
        onBlurCapture={(e) => {
          const filled = !!e.currentTarget.value;
          e.currentTarget.style.borderColor = filled
            ? "var(--mantine-color-green-4)"
            : "var(--mantine-color-orange-4)";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </NodeViewWrapper>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VariableChip = Node.create({
  name: "variableChip",
  group: "inline",
  inline: true,
  atom: true,

  addStorage() {
    return {
      config: {} as VariableConfig,
    };
  },

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const raw = element.getAttribute("data-variable");
          return raw ? normalizeVariableName(raw) : null;
        },
      },
      value: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-variable-value") || null,
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-variable]" }];
  },

  renderHTML({ node, HTMLAttributes }: { node: any; HTMLAttributes: any }) {
    const attrs: Record<string, string> = {
      "data-variable": node.attrs.name,
      style:
        "display: inline; background-color: var(--mantine-color-blue-1); " +
        "color: var(--mantine-color-blue-8); padding: 1px 6px; border-radius: 4px; " +
        "font-family: monospace; font-size: 0.85em; font-weight: 600; " +
        "white-space: nowrap; cursor: default; user-select: none;",
      contenteditable: "false",
    };
    if (node.attrs.value) {
      attrs["data-variable-value"] = node.attrs.value;
    }
    return [
      "span",
      mergeAttributes(HTMLAttributes, attrs),
      `{{${node.attrs.name}}}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChipView as any);
  },
});

export default VariableChip;
